/**
 * Framework-independent i18n core (unit-tested).
 */
import { ar } from './messages/ar';
import { en, type Messages } from './messages/en';

export type Language = 'ar' | 'en';
export type Direction = 'rtl' | 'ltr';

export const LANGUAGES: readonly Language[] = ['ar', 'en'] as const;
export const DEFAULT_LANGUAGE: Language = 'ar';
export const LANGUAGE_STORAGE_KEY = 'aqs.language';

/** Native names — the selector shows "العربية | English", never flags. */
export const LANGUAGE_NATIVE_NAMES: Record<Language, string> = {
  ar: 'العربية',
  en: 'English',
};

const catalogs: Record<Language, Messages> = { ar, en };

export function isLanguage(value: unknown): value is Language {
  return value === 'ar' || value === 'en';
}

export function directionOf(language: Language): Direction {
  return language === 'ar' ? 'rtl' : 'ltr';
}

export function messagesFor(language: Language): Messages {
  return catalogs[language];
}

/** Replaces {placeholders}. Unknown placeholders are left intact so gaps are visible in review. */
export function interpolate(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match,
  );
}

/** BCP 47 locale for formatting. Arabic uses Saudi conventions; Gregorian calendar for business dates. */
export function localeFor(language: Language): string {
  return language === 'ar' ? 'ar-SA-u-ca-gregory-nu-arab' : 'en-GB';
}

export const BUSINESS_TIME_ZONE = 'Asia/Riyadh';

export function formatDate(
  value: Date | string,
  language: Language,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
  timeZone: string = BUSINESS_TIME_ZONE,
): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(localeFor(language), { timeZone, ...options }).format(date);
}

export function formatTime(value: Date | string, language: Language, timeZone: string = BUSINESS_TIME_ZONE): string {
  return formatDate(value, language, { hour: 'numeric', minute: '2-digit' }, timeZone);
}

/** "5 minutes ago" / "منذ ٥ دقائق"; falls back to a date after a week. */
export function formatRelative(value: Date | string, language: Language, now: Date = new Date()): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(seconds);
  const rtf = new Intl.RelativeTimeFormat(localeFor(language), { numeric: 'auto' });
  if (abs < 45) return rtf.format(0, 'second');
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(seconds / 3600), 'hour');
  if (abs < 7 * 86_400) return rtf.format(Math.round(seconds / 86_400), 'day');
  return formatDate(date, language);
}

export function formatNumber(value: number, language: Language, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(localeFor(language), options).format(value);
}

export function formatCurrency(value: number, language: Language, currency = 'SAR'): string {
  return new Intl.NumberFormat(localeFor(language), {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Picks the language-specific column from a bilingual database row (name_en / name_ar). */
export function pickLocalized(row: object, base: string, language: Language): string {
  const fields = row as Record<string, unknown>;
  const preferred = fields[`${base}_${language}`];
  const fallback = fields[`${base}_${language === 'ar' ? 'en' : 'ar'}`];
  if (typeof preferred === 'string' && preferred.trim()) return preferred;
  return typeof fallback === 'string' ? fallback : '';
}

/** Resolves the initial language: saved choice → browser preference → default (Arabic). */
export function resolveInitialLanguage(saved: string | null, browserLanguages: readonly string[]): Language {
  if (isLanguage(saved)) return saved;
  for (const lang of browserLanguages) {
    const primary = lang.toLowerCase().split('-')[0];
    if (isLanguage(primary)) return primary;
  }
  return DEFAULT_LANGUAGE;
}
